/**
 * CoreStatus — live hardware/status heartbeat from the kiosk Core.
 *
 * Reads GET /api/agent/heartbeat?id=N (core_heartbeats). The Core's
 * AdminTelemetryService posts this every ~60s, replacing the legacy
 * push-agent's State.ini reporting. Auto-refreshes every 15s.
 */

import { useEffect, useState, useCallback } from 'react';
import { getCoreHeartbeat, type CoreHeartbeat } from '../api/client';

function dot(ok: boolean) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: '50%',
        marginRight: 6,
        background: ok ? '#1faa59' : '#c0392b',
      }}
    />
  );
}

/**
 * Уровень бумаги. Если принтер ответил по SNMP — показываем ЕГО число: это
 * измерение. Если промолчал — падаем на старую оценку «объявленная ёмкость
 * минус напечатано с последней заправки» и честно помечаем её тильдой: она
 * держится на том, что техник нажал «Papír», и 13.08 показывала 2000 из 2000
 * над пустым лотком.
 */
function paperText(
  isOut: boolean,
  sheets: number | null | undefined,
  capacity: number | null | undefined,
  full: number,
  used: number,
  stock?: number | null,
): string {
  if (isOut) return 'OUT';
  // Склад из сервис-меню (29.10.2026): техник кидает пачки ×500 / «лоток
  // заполнен», Core списывает напечатанное. Пока не пополняли — честное «?».
  if (typeof stock === 'number') return `${stock}${full > 0 ? ` / ${full}` : ''} sheets (stock)`;
  if (stock === null) return 'unknown — refill from the service menu';
  if (typeof sheets === 'number' && sheets >= 0) {
    return capacity && capacity > 0 ? `${sheets}/${capacity} sheets` : `${sheets} sheets`;
  }
  if (sheets === -1) return 'loaded (printer gives no count)';
  return `~${Math.max(0, full - used)}/${full} (estimate)`;
}

function paperTitle(sheets: number | null | undefined, stock?: number | null): string {
  if (typeof stock === 'number') return 'Paper stock kept by the kiosk: packs added in the service menu minus sheets printed';
  if (stock === null) return 'The technician has not refilled paper from the service menu since the update — no count yet';
  return typeof sheets === 'number'
    ? 'Measured by the printer over SNMP (Printer MIB prtInputCurrentLevel)'
    : 'Printer did not answer SNMP — falling back to the software estimate since the last "Papír" press';
}

function ageSeconds(iso: string | null | undefined): number {
  if (!iso) return Infinity;
  // Postgres timestamptz serializes as "2026-07-28 09:39:42.818+00": a space
  // instead of 'T' AND a bare "+00" offset that Date.parse rejects (it needs
  // ±HH:MM or Z). Normalize both, or every heartbeat reads "Infinitys ago".
  const s = iso.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
  const t = Date.parse(s);
  if (Number.isNaN(t)) return Infinity;
  return Math.round((Date.now() - t) / 1000);
}

export function CoreStatus({ machineId }: { machineId: number }) {
  const [hb, setHb] = useState<CoreHeartbeat | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getCoreHeartbeat(machineId)
      .then((h) => {
        setHb(h);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [machineId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const s = hb?.status ?? null;
  const age = ageSeconds(hb?.updated_at);
  const fresh = age < 180;

  return (
    <div className="card mb-6">
      <div className="card-header flex items-center justify-between" style={{ gap: 8 }}>
        <span>Core Status (live)</span>
        <button
          className="btn btn-secondary"
          style={{ padding: '2px 10px', fontSize: '0.8rem' }}
          onClick={load}
          disabled={loading}
        >
          {loading ? '…' : 'Refresh'}
        </button>
      </div>
      <div className="card-body">
        {error && <p className="text-secondary">Error: {error}</p>}
        {!error && !s && (
          <div className="empty-state">
            <p className="text-muted">No Core heartbeat yet. The Core posts one every ~60s.</p>
          </div>
        )}
        {s && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '8px 18px', fontSize: '0.9rem' }}>
            <div>{dot(fresh && s.online)} Backend: {s.online ? 'online' : 'offline'}</div>
            <div>{dot(s.printerBw === 'online')} Printer B/W: {s.printerBw}</div>
            <div>{dot(s.printerColor === 'online')} Printer Color: {s.printerColor}</div>
            <div>{dot(s.ups !== 'offline' && s.ups !== 'unknown')} UPS: {s.ups}</div>
            <div>{dot(!s.hopperLow)} Change hopper: {s.hopperLow ? 'LOW' : 'ok'}</div>
            {s.bill && (
              <div title={s.bill.denoms?.length ? `Denominations: ${s.bill.denoms.map((d) => d / 1).join('/')} Kč` : undefined}>
                {dot(!!s.bill.configured)} Bill validator:{' '}
                {!s.bill.present ? 'absent'
                  : s.bill.configured ? `configured (${s.bill.denoms.join('/')} Kč)`
                  : 'NOT configured'}
              </div>
            )}
            {s.coin && (
              <div>{dot(!!s.coin.present && s.coin.tubeCount > 0)} Coin acceptor: {s.coin.present ? `online · ${s.coin.tubeCount} tubes` : 'absent'}</div>
            )}
            {s.paper && (
              <>
                <div title={paperTitle(s.paper.bwSheets, s.paper.bwStock)}>
                  {dot(!s.paper.bwOut)} Paper B/W: {paperText(
                    s.paper.bwOut, s.paper.bwSheets, s.paper.bwCapacity,
                    s.paper.bwFull ?? s.paper.full ?? 0, s.paper.bwUsed, s.paper.bwStock)}
                </div>
                <div title={paperTitle(s.paper.colorSheets, s.paper.colorStock)}>
                  {dot(!s.paper.colorOut)} Paper Color: {paperText(
                    s.paper.colorOut, s.paper.colorSheets, s.paper.colorCapacity,
                    s.paper.colorFull ?? s.paper.full ?? 0, s.paper.colorUsed, s.paper.colorStock)}
                </div>
              </>
            )}
            <div>Uptime: {Math.floor(s.uptimeSec / 60)}m {s.uptimeSec % 60}s</div>
            <div>Mode: {s.mock ? 'mock' : 'real hardware'}</div>
            <div className="text-muted" style={{ gridColumn: '1 / -1', fontSize: '0.78rem' }}>
              updated {Number.isFinite(age) ? `${age}s ago` : 'time unknown'}{!fresh ? ' (stale)' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
